import { useEffect, useState, useCallback } from 'react';
import { Package, Clock, CheckCircle, XCircle, Truck, MapPin, Box } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Order status progression
const ORDER_STATUSES = ['confirmed', 'processing', 'shipped', 'delivered'];

const OrderTracker = ({ status }) => {
  const currentIndex = ORDER_STATUSES.indexOf(status);
  const isCancelled = status === 'cancelled' || status === 'failed';
  
  if (isCancelled) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Order {status === 'cancelled' ? 'Cancelled' : 'Failed'}</span>
        </div>
      </div>
    );
  }

  const steps = [
    { key: 'confirmed', label: 'Order Confirmed', icon: CheckCircle, description: 'Your order has been received' },
    { key: 'processing', label: 'Processing', icon: Box, description: 'Preparing your items' },
    { key: 'shipped', label: 'Shipped', icon: Truck, description: 'On the way to you' },
    { key: 'delivered', label: 'Delivered', icon: MapPin, description: 'Order delivered' }
  ];

  return (
    <div className="mt-6 pt-4 border-t">
      <h4 className="font-medium text-gray-700 mb-4">Order Tracking</h4>
      <div className="relative">
        {/* Progress line */}
        <div className="absolute top-5 left-5 right-5 h-0.5 bg-gray-200">
          <div 
            className="h-full bg-medical-primary transition-all duration-500"
            style={{ width: currentIndex >= 0 ? `${(currentIndex / (steps.length - 1)) * 100}%` : '0%' }}
          />
        </div>
        
        {/* Steps */}
        <div className="relative flex justify-between">
          {steps.map((step, index) => {
            const isCompleted = currentIndex >= index;
            const isCurrent = currentIndex === index;
            const Icon = step.icon;
            
            return (
              <div key={step.key} className="flex flex-col items-center" data-testid={`tracking-step-${step.key}`}>
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all duration-300 ${
                    isCompleted 
                      ? 'bg-medical-primary text-white' 
                      : 'bg-gray-200 text-gray-400'
                  } ${isCurrent ? 'ring-4 ring-medical-primary/20' : ''}`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div className="mt-2 text-center">
                  <p className={`text-sm font-medium ${isCompleted ? 'text-medical-primary' : 'text-gray-400'}`}>
                    {step.label}
                  </p>
                  <p className="text-xs text-gray-500 hidden sm:block">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API_URL}/orders`);
      setOrders(response.data);
    } catch (err) {
      setError('Unable to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
      case 'processing':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'shipped':
        return <Truck className="w-5 h-5 text-blue-600" />;
      case 'delivered':
        return <MapPin className="w-5 h-5 text-green-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'cancelled':
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Package className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
      case 'processing':
        return 'bg-green-100 text-green-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'delivered':
        return 'bg-emerald-100 text-emerald-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'confirmed': 'Order Confirmed',
      'processing': 'Processing',
      'shipped': 'Shipped',
      'delivered': 'Delivered',
      'pending': 'Pending',
      'cancelled': 'Cancelled',
      'failed': 'Failed'
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading orders...</div>;
  }

  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-24 h-24 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-semibold mb-2">No orders yet</h2>
          <p className="text-gray-600">Your order history will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-heading font-semibold mb-8">My Orders</h1>

        <div className="space-y-6">
          {orders.map((order) => (
            <div 
              key={order.id} 
              className="bg-white rounded-xl shadow-sm overflow-hidden" 
              data-testid={`order-${order.id}`}
            >
              {/* Order Header */}
              <div 
                className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-medical-primary/10 rounded-lg">
                      {getStatusIcon(order.status)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">Order #{order.id.substring(0, 8)}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {getStatusText(order.status)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {new Date(order.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 md:mt-0 text-right">
                    <p className="text-2xl font-semibold text-medical-primary">${order.total.toFixed(2)}</p>
                    <p className="text-sm text-gray-600">{order.items.length} item(s)</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>Click to {expandedOrder === order.id ? 'hide' : 'view'} details</span>
                  <svg 
                    className={`w-5 h-5 transition-transform ${expandedOrder === order.id ? 'rotate-180' : ''}`}
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedOrder === order.id && (
                <div className="px-6 pb-6 border-t">
                  {/* Order Tracking */}
                  <OrderTracker status={order.status} />
                  
                  {/* Shipping Address */}
                  {order.shipping_address && (
                    <div className="mt-6 pt-4 border-t">
                      <h4 className="font-medium text-gray-700 mb-2">Shipping Address</h4>
                      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                        <p className="font-medium text-gray-800">{order.shipping_address.full_name}</p>
                        <p>{order.shipping_address.address_line1}</p>
                        {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                        <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}</p>
                        {order.shipping_address.phone && <p>Phone: {order.shipping_address.phone}</p>}
                      </div>
                    </div>
                  )}

                  {/* Order Items */}
                  <div className="mt-6 pt-4 border-t">
                    <h4 className="font-medium text-gray-700 mb-3">Order Items</h4>
                    <div className="space-y-3">
                      {order.items.map((item) => (
                        <div key={item.product_id || item.product_name} className="flex justify-between items-center bg-gray-50 rounded-lg p-3">
                          <div>
                            <p className="font-medium">{item.product_name}</p>
                            <p className="text-sm text-gray-600">Quantity: {item.quantity} × ${item.price.toFixed(2)}</p>
                          </div>
                          <p className="font-medium text-medical-primary">${(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                    
                    {/* Order Summary */}
                    <div className="mt-4 pt-4 border-t flex justify-between items-center">
                      <span className="font-medium text-gray-700">Order Total</span>
                      <span className="text-xl font-semibold text-medical-primary">${order.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Orders;